/**
 * Prototype: Workspace settings IA
 * Access: /?test=workspace-settings-ia
 *
 * Scenes: home Extensions list, Basecamp page, Cursor page, sticky crumbs, search jumps.
 */

import { useMemo, useState } from 'react'
import { Search, ChevronRight } from 'lucide-react'
// ChevronRight used in Configure buttons and breadcrumb separators
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { ExtensionSettingsShell } from '@/components/integrations/_shared/ExtensionSettingsShell'
import { testIds } from '@/lib/test-ids'
import {
  EXTENSION_LABELS,
  EXTENSION_DESCRIPTIONS,
  EXTENSIONS_WITH_SETUP,
  WORKSPACE_SECTIONS,
  buildWorkspaceSearchHits,
  filterSettingsSearchHits,
  type WorkspaceSectionId,
} from '@/lib/settings-nav'
import { cn } from '@/lib/utils'

type SceneId = 'home' | 'basecamp' | 'cursor' | 'search'

const MOCK_EXTENSIONS = [
  'basecamp',
  'cursor',
  'google-drive',
  'zoom',
  'voice-memos',
  'knowledge-hub',
] as const

export function WorkspaceSettingsIaTest() {
  const [scene, setScene] = useState<SceneId>('home')
  const [section, setSection] = useState<WorkspaceSectionId>('extensions')
  const [extensionId, setExtensionId] = useState<string | null>(null)
  const [enabled, setEnabled] = useState<Record<string, boolean>>({
    basecamp: true,
    cursor: true,
    'google-drive': false,
    zoom: false,
    'voice-memos': true,
    'knowledge-hub': true,
  })
  const [searchQuery, setSearchQuery] = useState('')

  const hits = useMemo(() => {
    const tabHits = [
      {
        id: 'tab-workspaces',
        kind: 'tab' as const,
        tab: 'organizations',
        label: 'Workspaces',
        keywords: ['workspaces', 'organizations'],
      },
    ]
    return filterSettingsSearchHits([...tabHits, ...buildWorkspaceSearchHits()], searchQuery)
  }, [searchQuery])

  const openExtension = (id: string) => {
    setExtensionId(id)
    setSection('extensions')
    setScene(id === 'cursor' ? 'cursor' : id === 'basecamp' ? 'basecamp' : 'home')
  }

  const crumbs = (
    <div
      className="breadcrumb-nav shrink-0 border-b border-border/60 bg-background/95 px-4 py-2"
      data-testid={testIds.workspaceSettings.breadcrumbs}
    >
      <button
        type="button"
        className="breadcrumb-nav__link"
        onClick={() => {
          setExtensionId(null)
          setSection('extensions')
        }}
      >
        Workspaces
      </button>
      <ChevronRight className="h-4 w-4 shrink-0" />
      {extensionId ? (
        <>
          <button
            type="button"
            className="breadcrumb-nav__link"
            onClick={() => setExtensionId(null)}
          >
            Hop Studios
          </button>
          <ChevronRight className="h-4 w-4 shrink-0" />
          <span className="breadcrumb-nav__current">
            {EXTENSION_LABELS[extensionId] ?? extensionId}
          </span>
        </>
      ) : (
        <span className="breadcrumb-nav__current">Hop Studios</span>
      )}
    </div>
  )

  return (
    <div
      className="min-h-screen bg-muted/40 text-foreground"
      data-testid={testIds.workspaceSettingsIaTest.root}
    >
      <div className="border-b border-border bg-background px-4 py-3 flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold mr-2">Workspace settings IA</p>
        <div
          className="flex flex-wrap gap-1"
          data-testid={testIds.workspaceSettingsIaTest.sceneTabs}
        >
          {(
            [
              ['home', 'Extensions home'],
              ['basecamp', 'Basecamp page'],
              ['cursor', 'Cursor page'],
              ['search', 'Search'],
            ] as const
          ).map(([id, label]) => (
            <Button
              key={id}
              size="sm"
              variant={scene === id ? 'default' : 'outline'}
              onClick={() => {
                setScene(id)
                if (id === 'basecamp') openExtension('basecamp')
                else if (id === 'cursor') openExtension('cursor')
                else if (id === 'home') setExtensionId(null)
              }}
            >
              {label}
            </Button>
          ))}
        </div>
        <a href="/?test=index" className="text-xs text-muted-foreground ml-auto underline">
          Prototype index
        </a>
      </div>

      <div className="mx-auto max-w-3xl my-6 rounded-lg border border-border bg-background shadow-sm overflow-hidden flex flex-col h-[70vh]">
        <div className="px-4 py-2 text-sm font-medium border-b border-border/60 shrink-0">
          Workspaces
        </div>
        {crumbs}

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {scene === 'search' || searchQuery ? (
            <div className="space-y-2" data-testid={testIds.workspaceSettingsIaTest.search}>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search settings... (try listening, vsix, drive)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
              {hits.length > 0 && (
                <ul className="rounded-md border border-border divide-y">
                  {hits.map((hit) => (
                    <li key={hit.id}>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                        onClick={() => {
                          if (hit.kind === 'extension') openExtension(hit.extensionId)
                          else if (hit.kind === 'workspace-section') {
                            setExtensionId(null)
                            setSection(hit.section)
                            setScene('home')
                          } else if (hit.kind === 'tab' && hit.tab === 'account') {
                            // Production: Settings → Account. Harness stays workspace-scoped.
                            window.alert(
                              'Production jump: Settings → Account (Agents / hosted MCP tip).'
                            )
                          }
                          setSearchQuery('')
                        }}
                      >
                        {hit.label}
                        <span className="text-xs text-muted-foreground ml-2">{hit.kind}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          {!extensionId ? (
            <div className="flex gap-4">
              <nav
                className="w-36 shrink-0 space-y-0.5"
                data-testid={testIds.workspaceSettings.subNav}
              >
                {WORKSPACE_SECTIONS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSection(s.id)}
                    className={cn(
                      'w-full text-left text-sm px-2 py-1.5 rounded-md',
                      section === s.id
                        ? 'bg-accent font-medium'
                        : 'text-muted-foreground hover:bg-accent/50',
                    )}
                  >
                    {s.label}
                  </button>
                ))}
              </nav>
              <div className="flex-1 min-w-0 space-y-3">
                {section === 'extensions' && (
                  <div data-testid={testIds.workspaceSettings.sectionExtensions}>
                    <p className="text-sm text-muted-foreground mb-3">
                      Toggles only. Configure opens a dedicated extension page.
                    </p>
                    {MOCK_EXTENSIONS.map((id) => {
                      const hasSetup = (EXTENSIONS_WITH_SETUP as readonly string[]).includes(id)
                      return (
                        <div
                          key={id}
                          className="flex items-center justify-between gap-2 py-2 border-b border-border/40"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium">{EXTENSION_LABELS[id]}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {EXTENSION_DESCRIPTIONS[id]}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {enabled[id] && hasSetup ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs"
                                data-testid={testIds.workspaceSettings.configureExtension(id)}
                                onClick={() => openExtension(id)}
                              >
                                Configure
                                <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                              </Button>
                            ) : null}
                            <Switch
                              checked={Boolean(enabled[id])}
                              onCheckedChange={(v) =>
                                setEnabled((prev) => ({ ...prev, [id]: v }))
                              }
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
                {section === 'overview' && (
                  <p className="text-sm text-muted-foreground">Overview: name, icon, color (harness).</p>
                )}
                {section === 'team' && (
                  <p className="text-sm text-muted-foreground">Team: members / invite (harness).</p>
                )}
                {section === 'keys' && (
                  <p className="text-sm text-muted-foreground">Keys & secrets (harness).</p>
                )}
                {section === 'danger' && (
                  <p className="text-sm text-destructive">Danger: delete workspace (harness).</p>
                )}
              </div>
            </div>
          ) : extensionId === 'basecamp' ? (
            <ExtensionSettingsShell
              title="Basecamp"
              description="Reach into Basecamp from this workspace. Todos stay in Basecamp; Maater links work specs and pulls on demand."
              enabled={Boolean(enabled.basecamp)}
              onEnabledChange={(v) => setEnabled((p) => ({ ...p, basecamp: v }))}
              statusLabel={enabled.basecamp ? 'Enabled' : 'Disabled'}
              data-testid={testIds.workspaceSettings.extensionPage('basecamp')}
            >
              <p className="text-xs text-muted-foreground leading-relaxed rounded-md border border-border/50 bg-muted/10 px-4 py-3">
                Agent Basecamp writes need Connect here. MCP config lives under{' '}
                <strong className="font-medium text-foreground">Account</strong> settings
                (user-level).
              </p>
              <div className="rounded-md border border-border/50 p-4 text-sm text-muted-foreground">
                Harness: OAuth connect, linked account, Listening panel live in production.
              </div>
            </ExtensionSettingsShell>
          ) : extensionId === 'cursor' ? (
            <ExtensionSettingsShell
              title="Cursor IDE"
              description="Open Maater documents in Cursor and sync edits back."
              enabled={Boolean(enabled.cursor)}
              onEnabledChange={(v) => setEnabled((p) => ({ ...p, cursor: v }))}
              statusLabel={enabled.cursor ? 'Enabled' : 'Disabled'}
              data-testid={testIds.workspaceSettings.extensionPage('cursor')}
            >
              <ol className="list-decimal pl-5 text-sm space-y-2 text-muted-foreground">
                <li>Download the Maater Cursor VSIX and install in Cursor.</li>
                <li>Generate a personal access token below (production).</li>
                <li>
                  Prefer a named <code className="text-xs">.code-workspace</code> per client
                  (see product feature doc).
                </li>
              </ol>
              <Button size="sm" variant="outline" disabled>
                Download VSIX (harness)
              </Button>
            </ExtensionSettingsShell>
          ) : (
            <ExtensionSettingsShell
              title={EXTENSION_LABELS[extensionId] ?? extensionId}
              description={EXTENSION_DESCRIPTIONS[extensionId]}
              enabled={Boolean(enabled[extensionId])}
              onEnabledChange={(v) =>
                setEnabled((p) => ({ ...p, [extensionId]: v }))
              }
              data-testid={testIds.workspaceSettings.extensionPage(extensionId)}
            >
              <p className="text-sm text-muted-foreground">
                No dedicated setup UI in this harness scene yet.
              </p>
            </ExtensionSettingsShell>
          )}
        </div>
      </div>
    </div>
  )
}
